import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useSSEConnected } from '../../hooks/useSSE'
import { fetchProjects, fetchProjectTasks, fetchPersonas, fetchPersonaState } from '../../lib/api'
import PersonaAvatar, { isKnownPersona } from '../../components/PersonaAvatar'
import type { Project, ProjectTask, Persona } from '../../lib/types'

const FILTERS = ['active', 'in-review', 'completed', 'archived', 'all'] as const

interface HorizonItem {
  title: string; description?: string; image?: string; lead?: string; slug?: string; gathering_project?: string
}

// ═══ Multi-segment progress bar ═══
function TaskBar({ tasks }: { tasks: ProjectTask[] }) {
  const total = tasks.length
  if (total === 0) return null
  const completed = tasks.filter(t => t.status === 'completed').length
  const inProgress = tasks.filter(t => t.status === 'in_progress').length
  const blocked = tasks.filter(t => t.status === 'blocked').length
  return (
    <div className="w-full h-1 rounded-full overflow-hidden flex" style={{ background: 'var(--bg-body)' }}>
      <div style={{ width: `${(completed / total) * 100}%`, background: 'var(--success)' }} />
      <div style={{ width: `${(inProgress / total) * 100}%`, background: '#58a6ff' }} />
      <div style={{ width: `${(blocked / total) * 100}%`, background: 'var(--danger)' }} />
    </div>
  )
}

// ═══ Person chip ═══
function PersonChip({ name, isLead, persona }: { name: string; isLead: boolean; persona?: Persona }) {
  const human = !isKnownPersona(name)
  const inner = (
    <>
      <PersonaAvatar name={name} size={20} avatarImage={!!persona?.avatar_image} isHuman={human} />
      <span className="font-semibold" style={{ color: isLead ? 'var(--warning)' : 'var(--text-primary)' }}>
        {persona?.display_name || (name.charAt(0).toUpperCase() + name.slice(1))}
      </span>
      {isLead && <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>lead</span>}
    </>
  )
  const chipStyle = {
    background: 'var(--bg-body)',
    border: `1px solid ${isLead ? 'var(--warning)' : 'var(--border)'}`,
  }

  if (human) {
    return (
      <span className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs" style={chipStyle}>
        {inner}
      </span>
    )
  }

  return (
    <a
      href={`/terminal/${name}`}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs no-underline transition-colors hover:border-[#58a6ff]"
      style={chipStyle}
    >
      {inner}
    </a>
  )
}

// ═══ Project Card ═══
function ProjectCard({ project, personaMap, expanded, onToggle }: {
  project: Project & { tasks?: ProjectTask[]; communityName?: string }
  personaMap: Record<string, Persona>
  expanded: boolean
  onToggle: () => void
}) {
  const navigate = useNavigate()
  const tasks = project.tasks || []
  const completed = tasks.filter(t => t.status === 'completed').length
  const blocked = tasks.filter(t => t.status === 'blocked').length
  const blockerTasks = tasks.filter(t => t.status === 'blocker')
  const hasBlockers = blockerTasks.length > 0
  const incomplete = tasks.filter(t => t.status !== 'completed')

  // All people on this project
  const people = new Set<string>()
  if (project.lead) people.add(project.lead)
  ;(project.stakeholders || []).forEach(s => people.add(s))
  tasks.forEach(t => { if (t.assignee) people.add(t.assignee) })

  async function handleGroupChat() {
    try {
      const res = await fetch(`/api/projects/${project.id}/conversations`)
      const existing = await res.json()
      if (Array.isArray(existing) && existing.length > 0) {
        navigate(`/conversation/${existing[0].id}`)
        return
      }
    } catch {}
    // Create new
    const participants = ['joel', ...[...people].filter(p => p !== 'joel')]
    try {
      const res = await fetch('/api/conversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ participants, title: project.title + ' — Project Chat', type: 'group', project_id: project.id }),
      })
      const data = await res.json()
      if (data.id) navigate(`/conversation/${data.id}`)
    } catch {}
  }

  return (
    <div className="rounded-xl overflow-hidden" style={{
      background: 'var(--bg-surface)',
      border: hasBlockers ? '1px solid rgba(239,68,68,0.4)' : '1px solid var(--border)',
    }}>
      {/* Header */}
      <div className="px-4 pt-4 pb-3" style={{ borderBottom: '1px solid var(--bg-body)' }}>
        <Link to={`/project/${project.id}`} className="text-sm font-semibold no-underline hover:underline" style={{ color: 'var(--text-primary)' }}>
          {project.title}
        </Link>
        <div className="flex items-center gap-3 mt-1 text-xs flex-wrap">
          {hasBlockers && (
            <span
              className="px-2 py-0.5 rounded-full font-semibold"
              title={`${blockerTasks.length} task${blockerTasks.length > 1 ? 's' : ''} idle past 2h30m — needs triage`}
              style={{
                background: 'rgba(239,68,68,0.18)',
                color: '#ef4444',
                fontSize: 10,
                border: '1px solid rgba(239,68,68,0.4)',
              }}
            >
              🚫 BLOCKER {blockerTasks.length > 1 ? `× ${blockerTasks.length}` : ''}
            </span>
          )}
          <span className="px-2 py-0.5 rounded-full font-semibold" style={{
            background: project.status === 'active' ? 'rgba(63,185,80,0.15)' : 'rgba(210,153,34,0.15)',
            color: project.status === 'active' ? 'var(--success)' : 'var(--warning)',
            fontSize: 10,
          }}>{project.status}</span>
          {project.communityName && (
            <span className="px-2 py-0.5 rounded-full" style={{ background: 'rgba(88,166,255,0.1)', color: '#58a6ff', fontSize: 10 }}>
              {project.communityName}
            </span>
          )}
          {(project as any).tags?.includes('coding-lab') && (
            <span className="px-2 py-0.5 rounded-full" style={{ background: 'rgba(96,165,250,0.1)', color: '#60a5fa', fontSize: 10 }}>⚡ Coding Lab</span>
          )}
          {(project as any).tags?.includes('consciousness-lab') && (
            <span className="px-2 py-0.5 rounded-full" style={{ background: 'rgba(168,85,247,0.1)', color: '#a855f7', fontSize: 10 }}>🔬 Consciousness</span>
          )}
          {(project as any).tags?.includes('security-lab') && (
            <span className="px-2 py-0.5 rounded-full" style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: 10 }}>🛡️ Security</span>
          )}
          {(project as any).tags?.includes('data-lab') && (
            <span className="px-2 py-0.5 rounded-full" style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e', fontSize: 10 }}>📊 Data</span>
          )}
          {!(project as any).tags?.length && (
            <span className="px-2 py-0.5 rounded-full" style={{ background: 'rgba(107,114,128,0.1)', color: '#6b7280', fontSize: 10 }}>orphan</span>
          )}
          <span style={{ color: 'var(--text-muted)' }}>{completed}/{tasks.length} tasks done</span>
          {blocked > 0 && <span style={{ color: 'var(--danger)' }}>{blocked} blocked</span>}
        </div>
      </div>

      {/* People chips */}
      <div className="px-4 py-2.5 flex flex-wrap gap-1.5">
        {[...people].map(name => (
          <PersonChip key={name} name={name} isLead={name === project.lead} persona={personaMap[name]} />
        ))}
      </div>

      {/* Task progress */}
      <div className="px-4 pb-2">
        <TaskBar tasks={tasks} />
        {incomplete.length > 0 && (
          <>
            <button
              onClick={onToggle}
              className="text-xs mt-1.5"
              style={{ background: 'none', border: 'none', color: '#58a6ff', cursor: 'pointer' }}
            >
              {expanded ? '▾' : '▸'} {incomplete.length} open task{incomplete.length > 1 ? 's' : ''}
            </button>
            {expanded && (
              <div className="mt-1.5 flex flex-col gap-0.5">
                {incomplete.map(t => (
                  <Link key={t.id} to={`/project/${project.id}/task/${t.id}`} className="flex items-center gap-1.5 text-xs py-0.5 no-underline">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{
                      background: t.status === 'blocked' ? 'var(--danger)' : t.status === 'in_progress' ? '#58a6ff' : 'var(--text-muted)',
                    }} />
                    <span className="flex-1 truncate" style={{ color: 'var(--text-secondary)' }}>{t.title}</span>
                    {t.assignee && <span style={{ color: '#58a6ff', fontWeight: 500 }}>{personaMap[t.assignee]?.display_name || t.assignee}</span>}
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Actions */}
      <div className="px-4 pb-3 flex gap-1.5">
        <button onClick={handleGroupChat} className="text-xs px-2.5 py-1 rounded font-medium" style={{ background: 'var(--bg-elevated)', border: '1px solid #58a6ff', color: '#58a6ff', cursor: 'pointer' }}>
          Group Chat
        </button>
      </div>
    </div>
  )
}

// ═══ Main Page ═══
export default function ProjectsPage() {
  const { user } = useAuth()
  const sseOk = useSSEConnected()
  const isCommunity = user?.userType === 'community'
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const filter = searchParams.get('status') || 'active'
  const setFilter = (f: string) => setSearchParams(prev => { prev.set('status', f); return prev }, { replace: true })
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set())
  const showArchived = searchParams.get('archived') === 'true'
  const setShowArchived = (v: boolean) => setSearchParams(prev => { if (v) prev.set('archived', 'true'); else prev.delete('archived'); return prev }, { replace: true })
  const labFilter = searchParams.get('lab') || 'all'
  const setLabFilter = (f: string) => setSearchParams(prev => { if (f === 'all') prev.delete('lab'); else prev.set('lab', f); return prev }, { replace: true })
  const blockerFilter = (searchParams.get('blockers') || 'off') as 'off' | 'pinTop' | 'only'
  const setBlockerFilter = (f: 'off' | 'pinTop' | 'only') => setSearchParams(prev => { if (f === 'off') prev.delete('blockers'); else prev.set('blockers', f); return prev }, { replace: true })
  const [showCreate, setShowCreate] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [newOnHorizon, setNewOnHorizon] = useState(false)
  const [newPersonas, setNewPersonas] = useState<string[]>([])
  const [creating, setCreating] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState('')

  // Fetch available project templates
  const { data: projectTemplates } = useQuery({
    queryKey: ['project-templates'],
    queryFn: async () => {
      const res = await fetch('/api/project-templates')
      if (!res.ok) return []
      const data = await res.json()
      return data.templates || []
    },
  })

  function handleTemplateChange(templateId: string) {
    setSelectedTemplate(templateId)
    if (!templateId) return
    const tmpl = (projectTemplates || []).find((t: any) => t.id === templateId)
    if (!tmpl) return
    // Pre-fill description if empty
    if (!newDesc && tmpl.description) setNewDesc(tmpl.description)
    // Pre-select default lead + suggested stakeholders
    if (newPersonas.length === 0) {
      const suggested = [tmpl.default_lead, ...(tmpl.suggested_stakeholders || [])].filter(Boolean)
      setNewPersonas([...new Set(suggested)])
    }
  }

  async function handleCreateProject() {
    if (!newTitle.trim()) return
    setCreating(true)
    try {
      const tok = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
      const hdrs: Record<string, string> = { 'Content-Type': 'application/json' }
      if (tok) hdrs['Authorization'] = `Bearer ${tok}`

      const payload: Record<string, any> = {
        title: newTitle.trim(),
        description: newDesc.trim() || undefined,
        community_id: user?.communityId || undefined,
        stakeholders: newPersonas.length > 0 ? newPersonas : undefined,
        lead: newPersonas[0] || undefined,
        priority: newOnHorizon ? 'horizon' : 'medium',
      }
      if (selectedTemplate) payload.template_id = selectedTemplate

      await fetch('/api/projects', {
        method: 'POST',
        headers: hdrs,
        body: JSON.stringify(payload),
      })
      setNewTitle('')
      setNewDesc('')
      setNewOnHorizon(false)
      setNewPersonas([])
      setSelectedTemplate('')
      setShowCreate(false)
      queryClient.invalidateQueries({ queryKey: ['projects'] })
    } catch { /* ignore */ }
    setCreating(false)
  }

  // Core data — fetch active projects always
  const { data: projects, isLoading } = useQuery({
    queryKey: ['projects', filter],
    queryFn: () => fetchProjects(filter === 'all' ? undefined : filter),
    refetchInterval: sseOk ? false : 60_000, // SSE-conditional polling fallback
  })

  // Also fetch archived for the collapsed section
  const { data: archivedProjects } = useQuery({
    queryKey: ['projects', 'archived'],
    queryFn: () => fetchProjects('archived'),
    refetchInterval: sseOk ? false : 60_000, // SSE-conditional polling fallback
  })

  const { data: personas } = useQuery({ queryKey: ['personas'], queryFn: fetchPersonas })

  // Community-scoped personas for the create form
  const communityId = user?.communityId || null
  const token = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
  const authHeaders: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}
  const { data: availablePersonas } = useQuery({
    queryKey: ['availablePersonas', communityId],
    queryFn: async () => {
      if (isCommunity && communityId) {
        const res = await fetch(`/api/communities/${communityId}/personas`, { headers: authHeaders })
        if (!res.ok) return []
        const data = await res.json()
        const raw = data.personas || []
        return raw.map((p: string | Persona) =>
          typeof p === 'string' ? { name: p, display_name: p[0].toUpperCase() + p.slice(1) } as Persona : p
        )
      }
      return personas || []
    },
    enabled: isCommunity ? !!communityId : !!(personas),
  })
  useQuery({ queryKey: ['personaState'], queryFn: fetchPersonaState })
  const { data: communities } = useQuery({
    queryKey: ['communities'],
    queryFn: async () => { const r = await fetch('/api/communities'); return r.json() },
  })

  // Horizon items (separate API)
  const { data: horizonItems } = useQuery({
    queryKey: ['horizons'],
    queryFn: async () => {
      const r = await fetch('/api/horizons')
      if (!r.ok) return []
      return r.json() as Promise<HorizonItem[]>
    },
    enabled: isCommunity || filter === 'active',
  })

  // Standalone tasks query removed — all tasks now live in projects (quick-fix tagged)

  // All users see all projects they have access to (stakeholders or community-scoped)
  const allCommunityProjects = projects || []
  // Split horizon projects from active
  const projectList = allCommunityProjects.filter(p => (p as Project & { priority?: string }).priority !== 'horizon')
  const communityHorizonProjects = allCommunityProjects.filter(p => (p as Project & { priority?: string }).priority === 'horizon')
  const archivedList = archivedProjects || []
  const allProjectsList = [...projectList, ...archivedList]
  const { data: projectTasksMap } = useQuery({
    queryKey: ['allProjectTasks', allProjectsList.map(p => p.id).join(',')],
    queryFn: async () => {
      const map: Record<string, ProjectTask[]> = {}
      await Promise.all(allProjectsList.map(async p => {
        try {
          const d = await fetchProjectTasks(p.id)
          map[p.id] = d.tasks || []
        } catch { map[p.id] = [] }
      }))
      return map
    },
    enabled: allProjectsList.length > 0,
    staleTime: 15_000,
  })

  // Build persona map
  const personaMap: Record<string, Persona> = {}
  for (const p of (personas || [])) personaMap[p.name] = p

  // Build community.name map
  const communityNames: Record<string, string> = {}
  for (const p of (Array.isArray(communities) ? communities : [])) communityNames[p.id] = p.display_name || p.name

  // Enrich projects with tasks and community.names
  const enriched = projectList.map(p => ({
    ...p,
    tasks: (projectTasksMap || {})[p.id] || [],
    communityName: p.community_id ? (communityNames[p.community_id] || 'Community') : undefined,
  }))

  const horizon = horizonItems || []
  // const tasks removed — standalone tasks merged into projects

  function toggleExpand(id: string) {
    setExpandedProjects(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  }

  async function promoteHorizon(slug: string) {
    await fetch(`/api/horizons/${slug}/promote`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
  }

  async function archiveHorizon(slug: string) {
    await fetch(`/api/horizons/${slug}/archive`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
  }

  async function addHorizonItem() {
    const title = prompt('Horizon item title:')
    if (!title) return
    const description = prompt('Brief description:') || ''
    await fetch('/api/horizons', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title, description }) })
  }

  // Enrich archived projects too
  const enrichedArchived = archivedList.map(p => ({
    ...p,
    tasks: (projectTasksMap || {})[p.id] || [],
    communityName: p.community_id ? (communityNames[p.community_id] || 'Community') : undefined,
  }))

  return (
    <div className="max-w-5xl mx-auto px-6 py-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <Link to="/" className="text-xs px-2 py-1 rounded no-underline" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: '#58a6ff' }}>← {isCommunity ? 'Lobby' : 'The Gathering'}</Link>
        <h1 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Projects</h1>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="ml-auto text-xs px-3 py-1.5 rounded-md"
          style={{ background: 'var(--accent)', color: '#fff', border: 'none', cursor: 'pointer' }}
        >
          + New Project
        </button>
      </div>

      {/* Create Project Form */}
      {showCreate && (
        <div className="p-4 rounded-lg mb-4 mt-3" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
          {/* Template selector */}
          {projectTemplates && projectTemplates.length > 0 && (
            <div className="mb-3">
              <div className="text-xs mb-1.5" style={{ color: 'var(--text-muted)' }}>Template</div>
              <select
                value={selectedTemplate}
                onChange={e => handleTemplateChange(e.target.value)}
                className="w-full px-3 py-2 rounded text-sm outline-none"
                style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
              >
                <option value="">Blank project</option>
                {projectTemplates.map((t: any) => (
                  <option key={t.id} value={t.id}>{t.name} ({t.task_count} tasks)</option>
                ))}
              </select>
            </div>
          )}
          <input
            type="text"
            placeholder="Project title"
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
            className="w-full px-3 py-2 rounded text-sm mb-3 outline-none"
            style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
            autoFocus
          />
          <textarea
            placeholder="Description (optional)"
            value={newDesc}
            onChange={e => setNewDesc(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 rounded text-sm mb-3 outline-none resize-none"
            style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
          />
          {/* Persona picker */}
          <div className="mb-3">
            <div className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>Team members</div>
            <div className="flex flex-wrap gap-2">
              {(availablePersonas || []).map((p: Persona) => {
                const selected = newPersonas.includes(p.name)
                return (
                  <button
                    key={p.name}
                    onClick={() => setNewPersonas(prev =>
                      prev.includes(p.name) ? prev.filter(n => n !== p.name) : [...prev, p.name]
                    )}
                    className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs transition-colors"
                    style={{
                      background: selected ? 'var(--bg-elevated)' : 'transparent',
                      color: selected ? 'var(--text-primary)' : 'var(--text-muted)',
                      border: `1px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
                      cursor: 'pointer',
                    }}
                  >
                    <PersonaAvatar name={p.name} size={16} />
                    {p.display_name || p.name}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
              <input
                type="checkbox"
                checked={newOnHorizon}
                onChange={e => setNewOnHorizon(e.target.checked)}
                style={{ accentColor: 'var(--accent)' }}
              />
              On the Horizon (not yet active)
            </label>
            <div className="ml-auto flex gap-2">
              <button
                onClick={() => { setShowCreate(false); setNewTitle(''); setNewDesc(''); setNewOnHorizon(false) }}
                className="text-xs px-3 py-1.5 rounded-md"
                style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateProject}
                disabled={!newTitle.trim() || creating}
                className="text-xs px-4 py-1.5 rounded-md disabled:opacity-50"
                style={{ background: 'var(--accent)', color: '#fff', border: 'none', cursor: 'pointer' }}
              >
                {creating ? 'Creating...' : 'Create Project'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Community Horizon — projects with priority='horizon' */}
      {isCommunity && communityHorizonProjects.length > 0 && (
        <section className="mb-6 mt-4">
          <h2 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>On the Horizon</h2>
          <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollSnapType: 'x mandatory' }}>
            {communityHorizonProjects.map(p => (
              <div key={p.id} className="shrink-0 rounded-xl overflow-hidden" style={{ width: 260, background: 'var(--bg-surface)', border: '1px solid var(--border)', scrollSnapAlign: 'start' }}>
                <div className="p-4">
                  <div className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{p.title}</div>
                  {p.description && <div className="text-xs leading-relaxed mb-2" style={{ color: 'var(--text-muted)' }}>{p.description}</div>}
                  {p.lead && (
                    <div className="flex items-center gap-1.5 mb-2">
                      <PersonaAvatar name={p.lead} size={16} />
                      <span className="text-xs" style={{ color: '#58a6ff' }}>Lead: {p.lead}</span>
                    </div>
                  )}
                  <Link to={`/project/${p.id}`} className="text-xs px-2 py-1 rounded no-underline" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
                    View Project
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Horizon — from horizons API (visible to all users) */}
      {filter === 'active' && horizon.length > 0 && (
        <section className="mb-6 mt-4">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>On the Horizon</h2>
            <button onClick={addHorizonItem} className="ml-auto text-xs px-2.5 py-1 rounded" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-muted)', cursor: 'pointer' }}>+ Add</button>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollSnapType: 'x mandatory' }}>
            {horizon.filter(h => h.title).map(h => (
              <div key={h.slug || h.title} className="shrink-0 rounded-xl overflow-hidden transition-colors hover:border-[var(--border-accent)]" style={{ width: 240, background: 'var(--bg-surface)', border: '1px solid var(--border)', scrollSnapAlign: 'start' }}>
                {h.image && <img src={h.image} alt={h.title} className="w-full object-cover" style={{ height: 120 }} onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />}
                <div className="p-3">
                  <div className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{h.title}</div>
                  {h.description && <div className="text-xs leading-relaxed mb-1.5" style={{ color: 'var(--text-muted)' }}>{h.description}</div>}
                  {h.lead && <div className="text-xs mb-2" style={{ color: '#58a6ff' }}>Lead: {h.lead}</div>}
                  <div className="flex gap-1.5">
                    {h.gathering_project ? (
                      <Link to={`/project/${h.gathering_project}`} className="text-xs px-2 py-1 rounded no-underline" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}>View Project</Link>
                    ) : (
                      <button onClick={() => promoteHorizon(h.slug || '')} className="text-xs px-2 py-1 rounded" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-muted)', cursor: 'pointer' }}>Promote</button>
                    )}
                    <button onClick={() => archiveHorizon(h.slug || '')} className="text-xs px-2 py-1 rounded" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-muted)', cursor: 'pointer' }}>Archive</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Filters */}
      {(
        <div className="flex flex-col gap-2 mb-4">
          <div className="flex gap-1">
            {FILTERS.map(f => (
              <button key={f} onClick={() => setFilter(f)} className="text-xs px-3 py-1.5 rounded transition-colors capitalize" style={{
                background: filter === f ? 'rgba(88,166,255,0.1)' : 'var(--bg-elevated)',
                color: filter === f ? '#58a6ff' : 'var(--text-muted)',
                border: `1px solid ${filter === f ? '#58a6ff' : 'var(--border)'}`,
              }}>
                {f === 'in-review' ? 'In Review' : f}
              </button>
            ))}
          </div>
          <div className="flex gap-1 flex-wrap">
            {[
              { key: 'all', label: 'All Labs', color: 'var(--text-muted)' },
              { key: 'coding-lab', label: '⚡ Coding', color: '#60a5fa' },
              { key: 'consciousness-lab', label: '🔬 Consciousness', color: '#a855f7' },
              { key: 'security-lab', label: '🛡️ Security', color: '#ef4444' },
              { key: 'data-lab', label: '📊 Data', color: '#22c55e' },
              { key: 'quick-fix', label: '🔧 Quick Fix', color: '#f59e0b' },
              { key: 'orphan', label: 'Orphan', color: '#6b7280' },
            ].map(lab => (
              <button key={lab.key} onClick={() => setLabFilter(lab.key)} className="text-xs px-2.5 py-1 rounded transition-colors" style={{
                background: labFilter === lab.key ? `${lab.color}15` : 'var(--bg-elevated)',
                color: labFilter === lab.key ? lab.color : 'var(--text-muted)',
                border: `1px solid ${labFilter === lab.key ? lab.color : 'var(--border)'}`,
                cursor: 'pointer',
              }}>
                {lab.label}
              </button>
            ))}
          </div>
          {/* Blocker triage filter — projects with ≥1 task in 'blocker' state (idle past terminal threshold) */}
          <div className="flex gap-1 flex-wrap">
            {([
              { key: 'off', label: 'All Projects' },
              { key: 'pinTop', label: '🚫 Blockers First' },
              { key: 'only', label: '🚫 Blockers Only' },
            ] as const).map(opt => (
              <button
                key={opt.key}
                onClick={() => setBlockerFilter(opt.key)}
                className="text-xs px-2.5 py-1 rounded transition-colors"
                title={
                  opt.key === 'off' ? 'Show all projects in normal order' :
                  opt.key === 'pinTop' ? 'Sort projects with blocker-state tasks to the top' :
                  'Show only projects with at least one blocker-state task — Tack triage view'
                }
                style={{
                  background: blockerFilter === opt.key ? 'rgba(239,68,68,0.15)' : 'var(--bg-elevated)',
                  color: blockerFilter === opt.key ? '#ef4444' : 'var(--text-muted)',
                  border: `1px solid ${blockerFilter === opt.key ? '#ef4444' : 'var(--border)'}`,
                  cursor: 'pointer',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Loading...</div>
      ) : (
        <>
          {/* Project grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {(() => {
              const labFiltered = enriched.filter(p => {
                if (labFilter === 'all') return true
                const tags = (p as any).tags || []
                if (labFilter === 'orphan') return !tags.length
                return tags.includes(labFilter)
              })
              const withBlockerFlag = labFiltered.map(p => ({
                ...p,
                _hasBlockers: (p.tasks || []).some(t => t.status === 'blocker'),
              }))
              const blockerFiltered = blockerFilter === 'only'
                ? withBlockerFlag.filter(p => p._hasBlockers)
                : withBlockerFlag
              const sorted = blockerFilter === 'pinTop'
                ? [...blockerFiltered].sort((a, b) => Number(b._hasBlockers) - Number(a._hasBlockers))
                : blockerFiltered
              if (sorted.length === 0) {
                return <div className="text-sm py-12 text-center" style={{ color: 'var(--text-muted)' }}>
                  {blockerFilter === 'only' ? 'No projects have blocker-state tasks. Clean board.' : 'No projects found.'}
                </div>
              }
              return sorted.map(p => (
                <ProjectCard key={p.id} project={p} personaMap={personaMap} expanded={expandedProjects.has(p.id)} onToggle={() => toggleExpand(p.id)} />
              ))
            })()}
          </div>

          {/* Archived Projects */}
          {enrichedArchived.length > 0 && (
            <section className="mb-8">
              <button
                onClick={() => setShowArchived(!showArchived)}
                className="text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-2"
                style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <span>{showArchived ? '▾' : '▸'}</span>
                Completed ({enrichedArchived.length})
              </button>
              {showArchived && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {enrichedArchived.map(p => (
                    <ProjectCard key={p.id} project={p} personaMap={personaMap} expanded={expandedProjects.has(p.id)} onToggle={() => toggleExpand(p.id)} />
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Standalone Tasks section removed — all tasks now live in projects (quick-fix tagged) */}
        </>
      )}
    </div>
  )
}
